<script setup>
import { ref, reactive, defineEmits, computed } from 'vue'

import { useModal } from '@store/modal'
import { useTranslation } from '@composables/useTranslation'

const $modal = useModal();
const { t } = useTranslation();

// Props
const props = defineProps({
    modelValue: {
        type: String,
        default: ''
    },
    fields: {
        type: Object,
        default: () => ({
            name: 'Name',
            email: 'Email',
            phone: 'Phone',
            message: 'Message'
        })
    },
    form: {
        type: String,
        default: ''
    },
    defaultMessage: {
        type: String,
        default: ''
    },
    customTags: {
        type: [Object, Boolean],
        default: true // true = use global custom_tags, false = disable, object = use provided
    }
})

const emit = defineEmits(['update:modelValue'])

// State
const state = reactive({
    showInserts: false,
    presetPreview: false
})

// Refs
const template = ref(null)
const insert_vars = ref(null)

const previewPreset = computed(() => {
    let message = props.modelValue || '';

    // Replace variables with their values
    Object.keys(variables.value).forEach(key => {
        message = message.replace(new RegExp(`{${key}}`, 'g'), variables.value[key]);
    });

    // Apply formatting rules
    Object.values(formatRules).forEach(rule => {
        if (rule.pattern instanceof RegExp) {
            message = message.replace(new RegExp(rule.pattern, 'g'), (...args) => {
                if (typeof rule.html === 'function') {
                    return rule.html(...args);
                } else {
                    // For string-based replacements, replace $1, $2, etc with captured groups
                    return args.reduce((str, arg, index) => {
                        return str.replace(new RegExp('\\$' + index, 'g'), arg || '');
                    }, rule.html);
                }
            });
        }
    });
    
    // Clean up list HTML to properly group list items
    message = message
        .replace(/<\/ul><ul>/g, '') // Combine adjacent bullet lists
        .replace(/<\/ol><ol>/g, ''); // Combine adjacent numbered lists

    return message;
});

// Variables that can be inserted
const variables = computed(() => {
    const base = { ...props.fields };

    // Handle customTags prop
    if (props.customTags === false) {
        return base;
    } else if (props.customTags === true) {
        return { ...base, ...formychat_admin_vars.data.custom_tags || {} };
    } else if (typeof props.customTags === 'object') {
        return { ...base, ...props.customTags };
    }

    return base;
})

// Formatting rules
const formatRules = {
    bold: {
        pattern: /\*(.*?)\*/,
        code: '*$1*',
        html: '<strong>$1</strong>',
        label: `<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" class="bi bi-type-bold" viewBox="0 0 16 16">
  <path d="M8.21 13c2.106 0 3.412-1.087 3.412-2.823 0-1.306-.984-2.283-2.324-2.386v-.055a2.176 2.176 0 0 0 1.852-2.14c0-1.51-1.162-2.46-3.014-2.46H3.843V13zM5.908 4.674h1.696c.963 0 1.517.451 1.517 1.244 0 .834-.629 1.32-1.73 1.32H5.908V4.673zm0 6.788V8.598h1.73c1.217 0 1.88.492 1.88 1.415 0 .943-.643 1.449-1.832 1.449H5.907z"/>
</svg>`,
        description: 'Bold'
    },
    italic: {
        pattern: /_([^}]*)_/,
        code: '_$1_',
        html: '<em>$1</em>',
        label: `<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" class="bi bi-type-italic" viewBox="0 0 16 16">
  <path d="M7.991 11.674 9.53 4.455c.123-.595.246-.71 1.347-.807l.11-.52H7.211l-.11.52c1.06.096 1.128.212 1.005.807L6.57 11.674c-.123.595-.246.71-1.346.806l-.11.52h3.774l.11-.52c-1.06-.095-1.129-.211-1.006-.806z"/>
</svg>`,
        description: 'Italic'
    },
    del: {
        pattern: /~(.*?)~/,
        code: '~$1~',
        html: '<del>$1</del>',
        label: `<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" class="bi bi-type-strikethrough" viewBox="0 0 16 16">
  <path d="M6.333 5.686c0 .31.083.581.27.814H5.166a2.8 2.8 0 0 1-.099-.76c0-1.627 1.436-2.768 3.48-2.768 1.969 0 3.39 1.175 3.445 2.85h-1.23c-.11-1.08-.964-1.743-2.25-1.743-1.23 0-2.18.602-2.18 1.607zm2.194 7.478c-2.153 0-3.589-1.107-3.705-2.81h1.23c.144 1.06 1.129 1.703 2.544 1.703 1.34 0 2.31-.705 2.31-1.675 0-.827-.547-1.374-1.914-1.675L8.046 8.5H1v-1h14v1h-3.504c.468.437.675.994.675 1.697 0 1.826-1.436 2.967-3.644 2.967"/>
</svg>`,
        description: 'Strikethrough'
    },
    code: {
        pattern: /`(.*?)`/,
        code: '`$1`',
        html: '<code>$1</code>',
        label: `<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" class="bi bi-code-slash" viewBox="0 0 16 16">
  <path d="M10.478 1.647a.5.5 0 1 0-.956-.294l-4 13a.5.5 0 0 0 .956.294zM4.854 4.146a.5.5 0 0 1 0 .708L1.707 8l3.147 3.146a.5.5 0 0 1-.708.708l-3.5-3.5a.5.5 0 0 1 0-.708l3.5-3.5a.5.5 0 0 1 .708 0m6.292 0a.5.5 0 0 0 0 .708L14.293 8l-3.147 3.146a.5.5 0 0 0 .708.708l3.5-3.5a.5.5 0 0 0 0-.708l-3.5-3.5a.5.5 0 0 0-.708 0"/>
</svg>`,
        description: 'Inline Code'
    },
    monospace: {
        pattern: /```(.*?)```/,
        code: '```$1```',
        label: `<pre class="text-xs font-light">pre</pre>`,
        html: `<pre>$1</pre>`,
        description: 'Monospace'
    },
    bulletList: {
        pattern: /^[*-]\s(.*)$/gm,
        code: '* $1',
        html: (match, p1) => `<ul><li>${p1}</li></ul>`,
        label: `<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" class="bi bi-list-task" viewBox="0 0 16 16">
  <path fill-rule="evenodd" d="M2 2.5a.5.5 0 0 0-.5.5v1a.5.5 0 0 0 .5.5h1a.5.5 0 0 0 .5-.5V3a.5.5 0 0 0-.5-.5zM3 3H2v1h1z"/>
  <path d="M5 3.5a.5.5 0 0 1 .5-.5h9a.5.5 0 0 1 0 1h-9a.5.5 0 0 1-.5-.5M5.5 7a.5.5 0 0 0 0 1h9a.5.5 0 0 0 0-1zm0 4a.5.5 0 0 0 0 1h9a.5.5 0 0 0 0-1z"/>
  <path fill-rule="evenodd" d="M1.5 7a.5.5 0 0 1 .5-.5h1a.5.5 0 0 1 .5.5v1a.5.5 0 0 1-.5.5H2a.5.5 0 0 1-.5-.5zM2 7h1v1H2zm0 3.5a.5.5 0 0 0-.5.5v1a.5.5 0 0 0 .5.5h1a.5.5 0 0 0 .5-.5v-1a.5.5 0 0 0-.5-.5zm1 .5H2v1h1z"/>
</svg>`,
        description: 'Bullet List'
    },
    numberedList: {
        pattern: /^\d+\.\s(.*)$/gm,
        code: '1. $1',
        html: (match, p1) => `<ol><li>${p1}</li></ol>`,
        label: `<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" class="bi bi-list-ol" viewBox="0 0 16 16">
  <path fill-rule="evenodd" d="M5 11.5a.5.5 0 0 1 .5-.5h9a.5.5 0 0 1 0 1h-9a.5.5 0 0 1-.5-.5m0-4a.5.5 0 0 1 .5-.5h9a.5.5 0 0 1 0 1h-9a.5.5 0 0 1-.5-.5m0-4a.5.5 0 0 1 .5-.5h9a.5.5 0 0 1 0 1h-9a.5.5 0 0 1-.5-.5"/>
  <path d="M1.713 11.865v-.474H2c.217 0 .363-.137.363-.317 0-.185-.158-.31-.361-.31-.223 0-.367.152-.373.31h-.59c.016-.467.373-.787.986-.787.588-.002.954.291.957.703a.595.595 0 0 1-.492.594v.033a.615.615 0 0 1 .569.631c.003.533-.502.8-1.051.8-.656 0-1-.37-1.008-.794h.582c.008.178.186.306.422.309.254 0 .424-.145.422-.35-.002-.195-.155-.348-.414-.348h-.3zm-.004-4.699h-.604v-.035c0-.408.295-.844.958-.844.583 0 .96.326.96.756 0 .389-.257.617-.476.848l-.537.572v.03h1.054V9H1.143v-.395l.957-.99c.138-.142.293-.304.293-.508 0-.18-.147-.32-.342-.32a.33.33 0 0 0-.342.338zM2.564 5h-.635V2.924h-.031l-.598.42v-.567l.629-.443h.635z"/>
</svg>`,
        description: 'Numbered List'
    },
    newline: {
        pattern: /\n/,
        code: '\n',
        html: '<br>',
        label: '',
        description: 'New Line'
    },
    // Quote >.
    quote: {
        pattern: /^>\s(.*)$/gm,
        code: '> $1',
        html: '<blockquote>$1</blockquote>',
        label: `<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" class="bi bi-quote" viewBox="0 0 16 16">
  <path d="M12 12a1 1 0 0 0 1-1V8.558a1 1 0 0 0-1-1h-1.388q0-.527.062-1.054.093-.558.31-.992t.559-.683q.34-.279.868-.279V3q-.868 0-1.52.372a3.3 3.3 0 0 0-1.085.992 4.9 4.9 0 0 0-.62 1.458A7.7 7.7 0 0 0 9 7.558V11a1 1 0 0 0 1 1zm-6 0a1 1 0 0 0 1-1V8.558a1 1 0 0 0-1-1H4.612q0-.527.062-1.054.094-.558.31-.992.217-.434.559-.683.34-.279.868-.279V3q-.868 0-1.52.372a3.3 3.3 0 0 0-1.085.992 4.9 4.9 0 0 0-.62 1.458A7.7 7.7 0 0 0 3 7.558V11a1 1 0 0 0 1 1z"/>
</svg>`,
        description: 'Quote'
    }
}

// Methods
const applyRule = (rule) => {
    if (!template.value) return

    const input = template.value
    const start = input.selectionStart
    const end = input.selectionEnd
    const text = input.value
    const selected = text.substring(start, end)
    const hasSelection = start !== end

    let updatedText = ''
    let newStart = start
    let newEnd = end

    // Create a new input event
    const inputEvent = new InputEvent('input', {
        inputType: 'insertText',
        data: null,
        isComposing: false,
        bubbles: true
    })

    if (rule === formatRules.bulletList || rule === formatRules.numberedList) {
        // Handle list formatting
        const lines = selected.split('\n')
        const prefix = rule === formatRules.bulletList ? '* ' : '1. '
        
        // Check if lines are already formatted as a list
        const isAlreadyList = lines.every(line => 
            line.trim().startsWith('* ') || line.trim().startsWith('- ') || line.trim().match(/^\d+\.\s/)
        )

        if (isAlreadyList) {
            // Remove list formatting
            const unformatted = lines.map(line => 
                line.replace(/^[*-]\s|^\d+\.\s/, '')
            ).join('\n')
            updatedText = text.substring(0, start) + unformatted + text.substring(end)
            newEnd = start + unformatted.length
        } else {
            // Apply list formatting
            const formatted = lines.map((line, index) => {
                if (!line.trim()) return line
                if (rule === formatRules.numberedList) {
                    return `${index + 1}. ${line.trim()}`
                }
                return `* ${line.trim()}`
            }).join('\n')
            updatedText = text.substring(0, start) + formatted + text.substring(end)
            newEnd = start + formatted.length
        }
    } else {
        // Handle other formatting rules
        if (selected.match(rule.pattern)) {
            const unformatted = selected.replace(rule.pattern, '$1')
            updatedText = text.substring(0, start) + unformatted + text.substring(end)
            newEnd = start + unformatted.length
        } else {
            const markedUp = rule.code.replace('$1', selected || ' ')
            updatedText = text.substring(0, start) + markedUp + text.substring(end)

            if (!hasSelection) {
                const middleOffset = Math.floor(markedUp.length / 2)
                newStart = start + middleOffset
                newEnd = newStart
            } else {
                newEnd = start + markedUp.length
            }
        }
    }

    // Update the value and trigger input event
    input.value = updatedText
    input.dispatchEvent(inputEvent)
    emit('update:modelValue', updatedText)

    // Restore selection/cursor
    setTimeout(() => {
        input.focus()
        input.setSelectionRange(newStart, newEnd)
    }, 0)
}

const handleNewLine = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
        const input = template.value
        const text = input.value
        const cursorPos = input.selectionStart
        const textBeforeCursor = text.substring(0, cursorPos)
        const textAfterCursor = text.substring(cursorPos)
        
        // Get the last line before cursor
        const lines = textBeforeCursor.split('\n')
        const lastLine = lines[lines.length - 1]
        
        // Check if the last line is a list item
        const bulletMatch = lastLine.match(/^([*-])\s(.*)/)
        const numberMatch = lastLine.match(/^(\d+)\.\s(.*)/)
        
        const lastInput = bulletMatch?.[2] || numberMatch?.[2] || '';
        if (bulletMatch || numberMatch) {
            e.preventDefault()
            
            // If the last line is empty (except for the list marker), remove the marker
            if (!lastInput.trim()) {
                lines[lines.length - 1] = ''
                const newText = lines.join('\n') + textAfterCursor
                input.value = newText
                emit('update:modelValue', newText)
                input.setSelectionRange(cursorPos - lastLine.length, cursorPos - lastLine.length)
                return
            }
            
            // Keep the same bullet type (- or *) when continuing the list
            const newPrefix = bulletMatch ? 
                (bulletMatch[1] + ' ') : 
                `${parseInt(numberMatch[1]) + 1}. `;
            
            const newText = textBeforeCursor + '\n' + newPrefix + textAfterCursor
            input.value = newText
            emit('update:modelValue', newText)
            
            // Move cursor after the new list marker
            const newCursorPos = cursorPos + 1 + newPrefix.length
            input.setSelectionRange(newCursorPos, newCursorPos)
        }
    }
}

const insert = (variable) => {
    if (!template.value) return

    const input = template.value
    const start = input.selectionStart
    const end = input.selectionEnd
    const text = input.value
    const variableText = `{${variable}}`

    const updatedText = text.substring(0, start) + variableText + text.substring(end)

    // Create input event for undo stack
    const inputEvent = new InputEvent('input', {
        inputType: 'insertText',
        data: variableText,
        isComposing: false,
        bubbles: true
    })

    // Update value and trigger input event
    input.value = updatedText
    input.dispatchEvent(inputEvent)
    emit('update:modelValue', updatedText)

    // Move cursor after inserted variable
    setTimeout(() => {
        input.focus()
        const newPosition = start + variableText.length
        input.setSelectionRange(newPosition, newPosition)
    }, 0)

    state.showInserts = false
}

const keyboardEvents = (e) => {
    // Handle lists
    handleNewLine(e)
    
    // Ctrl/Cmd + B
    if ((e.ctrlKey || e.metaKey) && e.key === 'b') {
        e.preventDefault()
        applyRule(formatRules.bold)
    }

    // Ctrl/Cmd + I
    if ((e.ctrlKey || e.metaKey) && e.key === 'i') {
        e.preventDefault()
        applyRule(formatRules.italic)
    }

    // Ctrl/Cmd + S
    if ((e.ctrlKey || e.metaKey) && e.key === 's') {
        e.preventDefault()
        applyRule(formatRules.del)
    }
}

// Click outside handler
window.addEventListener('click', (e) => {
    if (insert_vars.value && !insert_vars.value.contains(e.target)) {
        state.showInserts = false
    }
})

// Reset
const reset = async () => {
    const isReset = await $modal.prompt({
        title: 'Reset Message Template',
        message: 'Are you sure you want to reset the message template?',
        ok: 'Yes, reset!'
    })

    if ( ! isReset.ok ) return;

    let resetText = ''

    // Use defaultMessage prop if provided
    if (props.defaultMessage) {
        resetText = props.defaultMessage
    } else {
        // Fallback to auto-generated format
        for (let field of Object.keys(props.fields)) {
            const shouldNewLine = props.form === 'formychat' && field === 'message'

            // Convert field name to title case by replacing special chars with spaces and capitalizing words
            const fieldName = field
                .replace(/[-_]/g, ' ') // Replace hyphens and underscores with spaces
                .split(' ')
                .map(word => word.charAt(0).toUpperCase() + word.slice(1))
                .join(' ')
            resetText += `${fieldName}:${shouldNewLine ? '\n' : ' '}{${field}}\n`
        }
    }

    template.value.value = resetText
    emit('update:modelValue', resetText)
}

</script>

<template>
    <div class="w-full relative flex flex-col gap-4">
        <div class="absolute z-10 right-3 top-3 cursor-pointer hover:opacity-100 transition"
            :class="{ 'opacity-50': !state.presetPreview }" @click="state.presetPreview = !state.presetPreview">
            <Tooltip>
                <svg xmlns="http://www.w3.org/2000/svg" class="fill-current w-5" viewBox="0 0 16 16">
                    <path d="M10.5 8a2.5 2.5 0 1 1-5 0 2.5 2.5 0 0 1 5 0" />
                    <path
                        d="M0 8s3-5.5 8-5.5S16 8 16 8s-3 5.5-8 5.5S0 8 0 8m8 3.5a3.5 3.5 0 1 0 0-7 3.5 3.5 0 0 0 0 7" />
                    <path v-if="!state.presetPreview"
                        d="M5.525 7.646a2.5 2.5 0 0 0 2.829 2.829zm4.95.708-2.829-2.83a2.5 2.5 0 0 1 2.829 2.829zm3.171 6-12-12 .708-.708 12 12z" />
                </svg>
                <template #content>
                    <div class="whitespace-nowrap">Toggle Preview of Preset Message</div>
                </template>
            </Tooltip>
        </div>
        <!-- Editor textarea -->
        <div class="ring-1 ring-gray-100 rounded" v-if="!state.presetPreview">
            <!-- Formatter toolbar -->
            <div class="border-b border-gray-100 py-2 px-2">
                <!-- Mobile: First row with first 6 buttons -->
                <div class="flex sm:hidden items-center gap-2 flex-wrap mb-2">
                    <template v-for="(rule, ruleId, index) in formatRules" :key="ruleId">
                        <span v-if="index < 6 && rule.label"
                            class="flex items-center justify-center cursor-pointer h-8 w-8 rounded border border-gray-200 hover:bg-gray-100 transition duration-75 bg-gray-50 active:scale-90 text-gray-700 shadow-sm"
                            v-html="rule.html.replace ? rule.html.replace(rule.html, rule.label) : rule.label"
                            :title="rule.description"
                            @click.prevent="applyRule(rule)" />
                    </template>
                </div>
                <!-- Mobile: Second row with remaining buttons -->
                <div class="flex sm:hidden items-center gap-2 flex-wrap">
                    <template v-for="(rule, ruleId, index) in formatRules" :key="ruleId">
                        <span v-if="index >= 6 && rule.label"
                            class="flex items-center justify-center cursor-pointer h-8 w-8 rounded border border-gray-200 hover:bg-gray-100 transition duration-75 bg-gray-50 active:scale-90 text-gray-700 shadow-sm"
                            v-html="rule.html.replace ? rule.html.replace(rule.html, rule.label) : rule.label"
                            :title="rule.description"
                            @click.prevent="applyRule(rule)" />
                    </template>
                    <!-- Mobile: Insert variables button -->
                    <div ref="insert_vars"
                        class="relative h-8 w-16 border border-gray-200 hover:bg-gray-100 transition duration-75 bg-gray-50 cursor-pointer sm:hidden">
                        <span @click="state.showInserts = !state.showInserts"
                            class="flex items-center justify-center w-full h-full"> {{ t('insert') }} </span>
                        <transition name="slide">
                            <div v-if="state.showInserts">
                                <div
                                    class="absolute translate-y-px top-full left-0 w-44 max-h-56 overflow-scroll bg-white shadow-lg rounded flex-col flex">
                                    <span v-for="v in Object.keys(variables)" :key="v"
                                        class="px-4 py-2 text-sm border-b border-gray-50 last:border-0 cursor-pointer hover:bg-gray-50 transition"
                                        @click="insert(v)"> {{ '{' + v + '}' }} </span>
                                </div>
                            </div>
                        </transition>
                    </div>
                </div>

                <!-- Desktop: Single row with all buttons -->
                <div class="hidden sm:flex items-center gap-2">
                    <template v-for="(rule, ruleId) in formatRules" :key="ruleId">
                        <span v-if="rule.label"
                            class="flex items-center justify-center cursor-pointer h-8 w-8 rounded border border-gray-200 hover:bg-gray-100 transition duration-75 bg-gray-50 active:scale-90 text-gray-700 shadow-sm"
                            v-html="rule.html.replace ? rule.html.replace(rule.html, rule.label) : rule.label"
                            :title="rule.description"
                            @click.prevent="applyRule(rule)" />
                    </template>
                    <!-- Desktop: Insert variables button -->
                    <div ref="insert_vars"
                        class="relative h-8 w-16 border border-gray-200 hover:bg-gray-100 transition duration-75 bg-gray-50 cursor-pointer">
                        <span @click="state.showInserts = !state.showInserts"
                            class="flex items-center justify-center w-full h-full"> {{ t('insert') }} </span>
                        <transition name="slide">
                            <div v-if="state.showInserts">
                                <div
                                    class="absolute translate-y-px top-full left-0 w-44 max-h-56 overflow-scroll bg-white shadow-lg rounded flex-col flex">
                                    <span v-for="v in Object.keys(variables)" :key="v"
                                        class="px-4 py-2 text-sm border-b border-gray-50 last:border-0 cursor-pointer hover:bg-gray-50 transition"
                                        @click="insert(v)"> {{ '{' + v + '}' }} </span>
                                </div>
                            </div>
                        </transition>
                    </div>
                </div>
            </div>
            <!-- Editor textarea -->
            <textarea
                class="w-full bg-gray-50 text-sm ring-1 ring-gray-200 outline-none border-0 rounded px-4 py-2 focus:ring focus:ring-indigo-500 focus:outline-none transition"
                style="resize: vertical !important; min-height: 80px !important; max-height: 600px !important; height: 80px;"
                @input="$emit('update:modelValue', $event.target.value)"
                ref="template"
                :value="props.modelValue"
                @keydown="keyboardEvents"
            >
            </textarea>

             <!-- Note: Some formatting rules are not supported in the WhatsApp Windows -->
             <div class="text-xs text-gray-500 px-3 py-2">
                <div class="flex items-center justify-between">
                    <div class="whitespace-nowrap"> 
                        {{ t('keyboard_shortcuts_ctrl') }}
                    </div>
                    <div>
                        <!-- Reset  -->
                        <Button class="text-xs bg-indigo-500 text-white px-2 py-1 rounded" @click="reset">{{ t('reset') }}</Button>
                    </div>
                </div>
                <div class="mt-1.5"> {{ t('whatsapp_formatting_note') }} <a class="text-blue-500 hover:underline" target="_blank" href="https://faq.whatsapp.com/539178204879377/?cms_platform=web">{{ t('learn_more') }}</a>.
                </div>
            </div>
        </div>
        <!-- Preview -->
        <div v-else class="border-0 ring-1 translate-y-px ring-gray-100 rounded">
            <div class="flex items-center gap-2 border-b border-gray-100 h-12 px-4 font-medium"> {{ t('preview_message') }} </div>
            <div class="px-4 py-2" v-html="previewPreset"></div>
        </div>
    </div>
</template>

<style>
.slide-enter-active,
.slide-leave-active {
    transition: all 0.3s ease;
}

.slide-enter-from,
.slide-leave-to {
    transform: translateY(-10px);
    opacity: 0;
}
</style>